import { computed, ref, shallowRef, type PropType, type Ref } from '@/composition/vue/compositionApi'
import type { Resolvers } from '@/services/types'
import {
  getLoggerService,
  getMonitoringService,
  getResolver,
  getRouterService,
  getTitlerManager,
  getWindowService,
} from '@/core/container/ioc'
import type { ResolverState } from '@/components/resolvers/types'
import type { Dict } from '@/types'
import { isAllDepsAreResolved } from '@/core/container/deps'
import type { Route } from '@/services/route/types'
import type { AnyComponent } from '@/components/types'

const loadedComponents = new Map<string, AnyComponent>()

export type RESOLVER_BASE_PROPS = {
  resolvers: Resolvers
  routeName: string
  tag?: keyof HTMLElementTagNameMap
}
export const resolversBaseProps = {
  resolvers: {
    type: Array as PropType<Resolvers>,
    required: true,
  },
  routeName: {
    type: String,
    required: true,
  },
} as const

export const useResolver = (routeName: Ref<string>) => {
  const routerService = getRouterService()

  const compKey = computed(
    () =>
      routerService.resolve({
        name: routeName.value,
      }).path,
  )

  return {
    compKey,
  }
}

/**
 * Composable for resolving components.
 *
 * @param routeName - The route name to resolve
 * @param resolverConfig - Array of resolvers to execute
 * @param nonBlockingResolvers - Array of resolvers to execute without waiting for them to resolve
 * @param componentAwaiter - Optional function to dynamically import a component
 * @returns Object with resolving state, results, and methods
 */
export const useResolvers = (
  routeName: string,
  resolverConfig: Resolvers,
  nonBlockingResolvers?: Resolvers,
  componentAwaiter?: () => Promise<AnyComponent>,
) => {
  const resolverService = getResolver()
  const logger = getLoggerService()
  const titlerManager = getTitlerManager()
  const monitoringService = getMonitoringService()

  const resolvingState = ref<ResolverState>('INITIAL')
  const resolveResult = ref<Dict>()
  const component = shallowRef<AnyComponent>()
  const routerService = getRouterService()
  const currentRoute = computed(() => routerService.getCurrentRoute())
  let isModulesFailed = false
  let isFailed = false

  const resolvingRoute = routerService.resolve({
    name: routeName,
    params: currentRoute.value?.params,
    query: currentRoute.value?.query,
    hash: currentRoute.value?.hash,
  }) as Route

  const handleError = (e: unknown) => {
    logger.error('resolver', 'Failed to resolve')
    logger.raw('resolver', e)
    resolveResult.value = {}
    isFailed = true
    if (!(e instanceof Error)) {
      throw e
    }
    monitoringService.logError(e)
    // eslint-disable-next-line no-console
    console.error(e)
  }

  const updateTitleAndHandleFailure = (fullReloadWhenFailed: boolean) => {
    titlerManager.updateTitle(currentRoute.value, currentRoute.value.meta?.titler?.params)

    if (isFailed && fullReloadWhenFailed) {
      getWindowService().reload()
    } else {
      resolvingState.value = isFailed ? 'FAILED' : 'RESOLVED'
    }
  }

  const checkModulesFailed = () => {
    if (isModulesFailed) {
      resolvingState.value = 'FAILED'
      return true
    }
    return false
  }

  const resolve = async (fullReloadWhenFailed = false) => {
    if (checkModulesFailed()) {
      return
    }

    resolvingState.value = 'INITIAL'
    try {
      resolveResult.value = await resolverService.resolveComponent(resolverConfig, resolvingRoute)
      isFailed = resolverService.isFailed(resolverConfig, routerService.getCurrentRoute())
    } catch (e) {
      handleError(e)
    }

    updateTitleAndHandleFailure(fullReloadWhenFailed)
  }

  const buildPromises = () => {
    const promises: Array<Promise<Dict | AnyComponent>> = []
    let needToWait = true

    // Add resolver promises
    if (Array.isArray(resolverConfig) && resolverConfig.length) {
      promises.push(resolverService.resolveComponent(resolverConfig, resolvingRoute))
    } else {
      promises.push(Promise.resolve({} as Dict))
      needToWait = false
    }

    // Add component awaiter promise
    if (componentAwaiter) {
      if (loadedComponents.has(routeName)) {
        promises.push(Promise.resolve(loadedComponents.get(routeName)))
      } else {
        promises.push(componentAwaiter())
        needToWait = true
      }
    }

    return { promises, needToWait }
  }

  const handleResolveResult = (result: Array<Dict | AnyComponent>, needToWait: boolean) => {
    resolveResult.value = result[0] as Dict

    if (componentAwaiter && result[1]) {
      // Check if the result is a dynamically imported module (has .default) or already loaded component
      const componentResult = result[1]
      component.value = componentResult.default ?? componentResult

      if (component.value) {
        loadedComponents.set(routeName, component.value)
      }
    }
  }

  const executeNonBlockingResolvers = () => {
    if (nonBlockingResolvers) {
      resolverService.resolveComponent(nonBlockingResolvers, resolvingRoute).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Non-blocking resolver failed', err)
      })
    }
  }

  const executeResolversWithNonBlocking = async () => {
    executeNonBlockingResolvers()

    const { promises, needToWait } = buildPromises()

    if (needToWait) {
      resolvingState.value = 'INITIAL'
    } else {
      resolvingState.value = 'RESOLVED'
    }

    const result = await Promise.all(promises)
    handleResolveResult(result, needToWait)

    isFailed = resolverService.isFailed(resolverConfig, routerService.getCurrentRoute())
  }

  const resolveWithNonBlockingResolvers = async (fullReloadWhenFailed = false) => {
    if (checkModulesFailed()) {
      return
    }

    try {
      await executeResolversWithNonBlocking()
    } catch (e) {
      handleError(e)
    }

    updateTitleAndHandleFailure(fullReloadWhenFailed)
  }

  const unresolve = async () => {
    stopBeforeEach()
    await resolverService.unresolveComponent(resolverConfig, resolvingRoute)
    if (nonBlockingResolvers) {
      resolverService.unresolveComponent(nonBlockingResolvers, resolvingRoute)
    }
  }

  const stopBeforeEach = routerService.getRouter().beforeEach(async (to) => {
    if (isAllDepsAreResolved(String(to.name), to.meta?.modules)) {
      return
    }
    try {
      // @todo - move this code to "resolve" method after fixed https://textmagic.atlassian.net/browse/CAR-3165
      await resolverService.resolveDeps(String(to.name), to.meta?.modules ?? [])
    } catch (e) {
      isModulesFailed = true
      throw e
    }
  })

  const retry = (fullReloadWhenFailed = true) => {
    return resolve(fullReloadWhenFailed)
  }

  return {
    resolvingState,
    resolveResult,
    resolve,
    resolveWithNonBlockingResolvers,
    unresolve,
    retry,
    component,
  }
}
